import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAtom } from 'jotai';
import { VirtuosoGrid } from 'react-virtuoso';
import { currentUserAtom } from '../../auth/AuthAtoms';
import { DEFAULT_CARD_FILTER_STATE, userCardsFilterAtom } from '../../atoms/cardFilterAtoms';
import Card from '../../components/Card';
import GLOBALS from '../../config/globals';
import { parseCardUpgrades, getUpgradeLevel, getUpgradeOptionsForCardType } from '../../utils/cardUpgrades';
import { getLastTeamAbbrev } from '../../helpers/teamHelpers';
import { fetchCardsByUserIDPaginated, fetchUserCardTeams } from '../../api/cards';
import useVariations from '../../hooks/useVariations';
import useSeries from '../../hooks/useSeries';
import LoadingSpinner from '../../components/LoadingSpinner';
import VariationTradeUpModal from '../../components/VariationTradeUpModal';
import TradeUpResultsModal from '../../components/TradeUpResultsModal';

const PAGE_SIZE = 50;

const mergeClassName = (...xs) => xs.filter(Boolean).join(' ');

const GridList = React.forwardRef(({ className, ...props }, ref) => (
    <div {...props} ref={ref} className={mergeClassName('user-cards-grid', className)} />
));
GridList.displayName = 'UserCardsGridList';

const GridItem = React.memo(({ children, className, ...props }) => (
    <div {...props} className={mergeClassName('user-cards-grid__virtuoso-item', className)}>
        {children}
    </div>
));
GridItem.displayName = 'UserCardsGridItem';

const gridComponents = { List: GridList, Item: GridItem };

const UserCardsPage = () => {
    const [currentUser] = useAtom(currentUserAtom);
    const variations = useVariations();
    const series = useSeries();
    const seriesYears = [...new Set(series.map(s => s.year).filter(Boolean))].sort((a, b) => b - a);

    // Team filter options
    const [teams, setTeams] = useState([]);

    // Card data
    const [cards, setCards] = useState([]);
    const [totalCards, setTotalCards] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const offsetRef = useRef(0);

    // Generation counter — prevents stale fetch responses from corrupting state
    // when filters change while a request is in-flight
    const generationRef = useRef(0);

    // Filter panel visibility (mobile)
    const [filtersOpen, setFiltersOpen] = useState(false);

    // Trade-up modals
    const [showTradeUp, setShowTradeUp] = useState(false);
    const [tradeResults, setTradeResults] = useState(null);

    // Loading states
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const loadingMoreRef = useRef(false);
    const [error, setError] = useState(null);

    // Filters & sort (persisted via Jotai atom across navigation)
    const [filterState, setFilterState] = useAtom(userCardsFilterAtom);
    const { filters, sort, sortDir } = filterState;

    const setFilters = useCallback((newFilters) => {
        setFilterState(prev => ({ ...prev, filters: newFilters }));
    }, [setFilterState]);

    const activeFilterCount = useMemo(() => {
        const { filters: def, sort: defSort, sortDir: defDir } = DEFAULT_CARD_FILTER_STATE;
        let count = Object.keys(def).filter(k => filters[k] !== def[k]).length;
        if (sort !== defSort || sortDir !== defDir) count += 1;
        return count;
    }, [filters, sort, sortDir]);

    const hasActiveFilters = activeFilterCount > 0;

    const resetFilters = useCallback(() => {
        setFilterState(DEFAULT_CARD_FILTER_STATE);
    }, [setFilterState]);

    // Build query params from current filters + sort
    const buildParams = useCallback((offset) => {
        const params = { limit: PAGE_SIZE, offset, sort, dir: sortDir };
        if (filters.year) params.year = filters.year;
        if (filters.rating) params.rating = filters.rating;
        if (filters.team) params.teams = filters.team;
        if (filters.type) params.type = filters.type;
        if (filters.variation) params.variation = filters.variation;
        if (filters.position) params.position = filters.position;
        if (filters.canLevelUp) params.canLevelUp = filters.canLevelUp;
        return params;
    }, [filters, sort, sortDir]);

    // Fetch a page of cards
    const fetchPage = useCallback(async (offset, isReset = false) => {
        if (isReset) {
            setInitialLoading(true);
        } else {
            setLoadingMore(true);
        }
        setError(null);

        // Capture current generation so we can discard stale responses
        const gen = generationRef.current;

        try {
            const params = buildParams(offset);
            const data = await fetchCardsByUserIDPaginated(params);

            // If filters changed while this request was in-flight, discard
            if (gen !== generationRef.current) return;

            const mapped = data.cards.map(ci => ({
                ...ci.Card,
                amount: ci.amount,
                inventoryID: ci.inventoryID,
                variationID: ci.variationID || null,
                CardVariation: ci.CardVariation || null,
                CardInventory: { displayKey: ci.displayKey, upgrades: ci.upgrades, exp: ci.exp },
            }));

            if (isReset) {
                setCards(mapped);
            } else {
                setCards(prev => [...prev, ...mapped]);
            }
            setTotalCards(data.totalCards);
            setHasMore(data.hasMore);
            offsetRef.current = offset + mapped.length;
        } catch (err) {
            if (gen !== generationRef.current) return;
            setError('Error fetching cards');
            console.error('Error fetching cards:', err);
        } finally {
            if (gen === generationRef.current) {
                setInitialLoading(false);
                setLoadingMore(false);
                loadingMoreRef.current = false;
            }
        }
    }, [buildParams]);

    // Stable ref to fetchPage for Virtuoso callback
    const fetchPageRef = useRef(fetchPage);
    fetchPageRef.current = fetchPage;

    // Fetch team filter options on mount
    useEffect(() => {
        if (!currentUser) return;
        fetchUserCardTeams().then(setTeams);
    }, [currentUser]);

    // Initial load + re-fetch when filters/sort change
    useEffect(() => {
        if (!currentUser) return;
        generationRef.current += 1; // invalidate any in-flight requests
        offsetRef.current = 0;
        loadingMoreRef.current = false;
        setHasMore(true);
        window.scrollTo(0, 0);
        fetchPage(0, true);
    }, [currentUser, fetchPage]);

    const loadMore = useCallback(() => {
        if (!hasMore || loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        fetchPageRef.current(offsetRef.current);
    }, [hasMore]);

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
    };

    const handleSortChange = (newSort) => {
        if (newSort === sort) {
            setFilterState(prev => ({
                ...prev,
                sortDir: prev.sortDir === 'desc' ? 'asc' : 'desc',
            }));
        } else {
            setFilterState(prev => ({ ...prev, sort: newSort, sortDir: 'desc' }));
        }
    };

    const renderCard = useCallback((index) => {
        const card = cards[index];
        if (!card) return null;

        const upgrades = parseCardUpgrades(card.CardInventory?.upgrades);
        const isBatter = card.type === 'Batter';
        const level = getUpgradeLevel(upgrades, getUpgradeOptionsForCardType(isBatter));
        const exp = card.CardInventory?.exp ?? 0;
        const cost = GLOBALS.CARD_UPGRADE_COST(level);
        const canLevelUp = exp >= cost;

        return (
            <Link
                className={`user-cards-grid__item${canLevelUp ? ' card-can-upgrade' : ''}`}
                to={`/cards/inventory/${card.inventoryID}`}
            >
                <Card card={card} variation={card.CardVariation || null} showLabel hoverFlip />
            </Link>
        );
    }, [cards]);

    return (
        <div className="user-cards-page">
            {error && <p className="error">{error}</p>}
            {initialLoading ? (
                <LoadingSpinner />
            ) : (
                <>
                    <h2>
                        My Cards ({totalCards}){' '}
                        <Link to="/cards/sell" className="button">Sell Cards</Link>
                        <Link to="/binders" className="button">My Binders</Link>
                        <button className="button" onClick={() => setShowTradeUp(true)}>Trade Up Variations</button>
                        <button
                            className="button card-filter__toggle"
                            onClick={() => setFiltersOpen(p => !p)}
                        >
                            {filtersOpen ? 'Hide Filters' : 'Filters'}
                            {!filtersOpen && hasActiveFilters && (
                                <span className="card-filter__badge">{activeFilterCount}</span>
                            )}
                        </button>
                    </h2>
                    <div className={`card-filter${filtersOpen ? ' card-filter--open' : ''}`}>
                        <label>
                            Year:
                            <select
                                value={filters.year}
                                onChange={e => handleFilterChange({ ...filters, year: e.target.value })}
                            >
                                <option value="">All years</option>
                                {seriesYears.map(y => (
                                    <option key={y} value={String(y)}>{y}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            Rating:
                            <select
                                value={filters.rating}
                                onChange={e => handleFilterChange({ ...filters, rating: e.target.value })}
                            >
                                <option value="">All ratings</option>
                                <option value="96">96+ (Uranium)</option>
                                <option value="86">86+ (Titanium)</option>
                                <option value="76">76+ (Gold)</option>
                                <option value="66">66+ (Silver)</option>
                                <option value="56">56+ (Bronze)</option>
                            </select>
                        </label>
                        <label>
                            Type:
                            <select
                                value={filters.type}
                                onChange={e => handleFilterChange({ ...filters, type: e.target.value })}
                            >
                                <option value="">All</option>
                                <option value="Batter">Batters</option>
                                <option value="Pitcher">Pitchers</option>
                            </select>
                        </label>
                        <label>
                            Team:
                            <select
                                value={filters.team}
                                onChange={e => handleFilterChange({ ...filters, team: e.target.value })}
                            >
                                <option value="">All teams</option>
                                {[...new Map(teams.map(t => [getLastTeamAbbrev(t), t])).entries()]
                                    .sort(([a], [b]) => a.localeCompare(b))
                                    .map(([display, raw]) => (
                                        <option key={raw} value={raw}>{display}</option>
                                    ))}
                            </select>
                        </label>
                        <label>
                            Position:
                            <select
                                value={filters.position}
                                onChange={e => handleFilterChange({ ...filters, position: e.target.value })}
                            >
                                <option value="">All</option>
                                <option value="C">C</option>
                                <option value="1B">1B</option>
                                <option value="2B">2B</option>
                                <option value="3B">3B</option>
                                <option value="SS">SS</option>
                                <option value="OF">OF</option>
                                <option value="DH">DH Only</option>
                                <option value="SP">SP</option>
                                <option value="RP">RP</option>
                            </select>
                        </label>
                        <label>
                            Variation:
                            <select
                                value={filters.variation}
                                onChange={e => handleFilterChange({ ...filters, variation: e.target.value })}
                            >
                                <option value="">All</option>
                                <option value="base">Base</option>
                                <option value="non-base">Non-Base</option>
                                {variations.map(v => (
                                    <option key={v.variationID} value={String(v.variationID)}>
                                        {v.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <button
                            className={`card-filter__pill${filters.canLevelUp ? ' card-filter__pill--active' : ''}`}
                            onClick={() => handleFilterChange({ ...filters, canLevelUp: filters.canLevelUp ? '' : '1' })}
                        >
                            Level
                        </button>
                        <label>
                            Sort:
                            <select
                                value={sort}
                                onChange={e => handleSortChange(e.target.value)}
                            >
                                <option value="rating">Rating</option>
                                <option value="name">Name</option>
                                <option value="year">Year</option>
                                <option value="speed">Speed</option>
                                <option value="stamina">Stamina</option>
                                <option value="created">Recently Acquired</option>
                            </select>
                        </label>
                        {hasActiveFilters && (
                            <button className="button button--reset-filters" onClick={resetFilters}>
                                Reset
                            </button>
                        )}
                    </div>

                    <VirtuosoGrid
                        useWindowScroll
                        totalCount={cards.length}
                        overscan={800}
                        components={gridComponents}
                        itemContent={renderCard}
                        computeItemKey={index => {
                            const card = cards[index];
                            return card ? card.inventoryID : index;
                        }}
                        endReached={loadMore}
                    />

                    {loadingMore && (
                        <div className="loading-spinner--small">
                            <LoadingSpinner />
                        </div>
                    )}

                    {!hasMore && cards.length > 0 && (
                        <p className="user-cards-end">
                            Showing all {totalCards} cards
                        </p>
                    )}

                    {!hasMore && cards.length === 0 && (
                        <p className="user-cards-end">
                            No cards found
                        </p>
                    )}
                </>
            )}

            {showTradeUp && (
                <VariationTradeUpModal
                    onClose={() => setShowTradeUp(false)}
                    onTradeComplete={setTradeResults}
                />
            )}
            {tradeResults && (
                <TradeUpResultsModal
                    results={tradeResults?.results || []}
                    onClose={() => setTradeResults(null)}
                    onTradeAgain={() => {
                        setTradeResults(null);
                        setShowTradeUp(true);
                    }}
                />
            )}
        </div>
    );
};

export default UserCardsPage;
